import Head from 'next/head';
import { Blog } from 'components/blog/blog.types';
import {
  LIBUM_IMAGE_PARTIAL_URL,
  LIBUM_URL,
  LINKEDIN_URL,
  GITHUB_URL,
  DISCORD_URL,
  CRUNCHBASE_URL,
  CBINSIGHTS_URL,
} from 'shared/constants/globals';

type ProductSchemaData = {
  name: string;
  description: string;
  category: string;
  operatingSystem: string;
  image?: string;
  keywords?: string;
  featureList?: string[];
};

type Breadcrumb = {
  name: string;
  url: string;
};

type ContainerProps = {
  title: string;
  description: string;
  keywords: string;
  url: string;
  socialCard: string;
  children: React.ReactNode;
  product?: ProductSchemaData;
  breadcrumbs?: Breadcrumb[];
  blog?: Blog;
  faq?: { question: string; answer: string }[];
  noIndex?: boolean;
};

export default function Container({
  title,
  description,
  keywords,
  url,
  socialCard,
  children,
  product,
  breadcrumbs,
  blog,
  faq,
  noIndex,
}: ContainerProps) {
  const socialCardImage = `${LIBUM_IMAGE_PARTIAL_URL}_${socialCard}.png`;
  const organizationId = `${LIBUM_URL}/#organization`;
  const webSiteId = `${LIBUM_URL}/#website`;
  const webPageId = `${url}#webpage`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,
    name: 'Libum',
    legalName: 'Libum, LLC',
    alternateName: ['Libum LLC', 'Libum Software'],
    disambiguatingDescription:
      'Libum, LLC is a credit union software company for Jack Henry Symitar platforms — distinct from the ancient Roman cake of the same name.',
    url: LIBUM_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${LIBUM_URL}/images/icon-512x512.png`,
      width: 512,
      height: 512,
    },
    description:
      'Libum is a software company driving the modernization of credit unions with innovative tools for Jack Henry Symitar platforms.',
    slogan: 'Making credit union software a piece of cake.',
    foundingDate: '2020',
    email: 'support@libum.io',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1401 Lavaca St PMB 41479',
      addressLocality: 'Austin',
      addressRegion: 'TX',
      postalCode: '78701',
      addressCountry: 'US',
    },
    knowsAbout: [
      'Credit union software',
      'Jack Henry Symitar',
      'PowerOn development',
      'SymXchange',
      'Core banking integrations',
      'AI for the Symitar core',
      'Model Context Protocol (MCP)',
      'Natural language SymXchange access',
      'AI agents for credit unions',
    ],
    sameAs: [LINKEDIN_URL, GITHUB_URL, DISCORD_URL, CRUNCHBASE_URL, CBINSIGHTS_URL],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'sales@libum.io',
    },
  };

  const softwareAppSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': `${url}#software`,
        name: product.name,
        description: product.description,
        url,
        image: product.image || socialCardImage,
        applicationCategory: product.category,
        operatingSystem: product.operatingSystem,
        ...(product.keywords ? { keywords: product.keywords } : {}),
        ...(product.featureList ? { featureList: product.featureList } : {}),
        brand: {
          '@type': 'Brand',
          name: 'Libum',
        },
        publisher: {
          '@type': 'Organization',
          '@id': organizationId,
        },
      }
    : null;

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': webSiteId,
    name: 'Libum',
    url: LIBUM_URL,
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
    },
    inLanguage: 'en-US',
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': product ? 'ItemPage' : 'WebPage',
    '@id': webPageId,
    url,
    name: title,
    description,
    isPartOf: {
      '@type': 'WebSite',
      '@id': webSiteId,
    },
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
    },
    inLanguage: 'en-US',
  };

  const siteNavigationSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'Products',
      url: `${LIBUM_URL}/products`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'PowerOn Pro',
      url: `${LIBUM_URL}/poweron-pro`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'PowerOn Pipelines',
      url: `${LIBUM_URL}/poweron-pipelines`,
    },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'NexumAPI', url: `${LIBUM_URL}/nexum` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'Glaci', url: `${LIBUM_URL}/glaci` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'About', url: `${LIBUM_URL}/about` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'Blog', url: `${LIBUM_URL}/blog` },
  ];

  const breadcrumbSchema = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: LIBUM_URL },
          ...breadcrumbs.map((bc, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: bc.name,
            item: bc.url,
          })),
        ],
      }
    : null;

  const blogPostingSchema = blog
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${url}#blogposting`,
        headline: blog.title,
        description: blog.description,
        datePublished: blog.date.toISOString().split('T')[0],
        dateModified: blog.date.toISOString().split('T')[0],
        author: { '@type': 'Person', name: blog.author, url: blog.authorLink },
        publisher: { '@type': 'Organization', '@id': organizationId },
        mainEntityOfPage: { '@type': 'WebPage', '@id': webPageId },
        image: socialCardImage,
        keywords: blog.keywords?.join(', '),
      }
    : null;

  const faqSchema =
    faq && faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          '@id': `${url}#faq`,
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  return (
    <>
      <Head>
        <link rel="icon" href="/images/favicon.ico" sizes="48x48" />
        <link rel="icon" type="image/svg+xml" href="/images/favicon.svg" />
        <link rel="icon" type="image/png" sizes="96x96" href="/images/favicon-96x96.png" />
        <link rel="icon" type="image/png" sizes="48x48" href="/images/favicon-48x48.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/images/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="Libum" />
        <link rel="manifest" href="/site.webmanifest" />
        <title>{title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="description" content={description} />
        {keywords !== '' && <meta name="keywords" content={keywords} />}
        {blog && <meta name="author" content={blog.author} />}
        <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
        <link rel="canonical" href={url} />
        {/* Open Graph */}
        <meta property="og:type" content={blog ? 'article' : 'website'} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Libum" />
        {keywords !== '' && <meta property="og:keywords" content={keywords} />}
        <meta property="og:image" content={socialCardImage} />
        <meta property="og:image:secure_url" content={socialCardImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${title} social preview`} />
        {blog && <meta property="article:published_time" content={blog.date.toISOString()} />}
        {blog && <meta property="article:modified_time" content={blog.date.toISOString()} />}
        {blog && <meta property="article:author" content={blog.authorLink} />}
        {blog && <meta property="article:section" content="Credit union technology" />}
        {blog?.keywords?.map((keyword) => (
          <meta key={keyword} property="article:tag" content={keyword} />
        ))}
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={socialCardImage} />
        <meta name="twitter:image:alt" content={`${title} social preview`} />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(siteNavigationSchema) }} />
        {softwareAppSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppSchema) }} />
        )}
        {breadcrumbSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        )}
        {blogPostingSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }} />
        )}
        {faqSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        )}
      </Head>
      {children}
    </>
  );
}
